/**
 * Carte du mode course — EF-IHM-003, EF-MOD-003, EF-RBK-002, EF-RBK-011.
 *
 * Un POI par carte. Les trois valeurs restantes sont comptées depuis la
 * position courante ; l'heure estimée, elle, reste une heure d'horloge, car
 * c'est elle qui se compare à un horaire d'ouverture. Chaque valeur occupe sa
 * ligne, précédée de son libellé et alignée à droite en chiffres tabulaires :
 * un seul balayage du regard suffit.
 */

import type { JSX } from 'preact';
import { categoryFamily, categoryLabel } from '../../core/categories';
import { nominalCategory, type Poi, type RoadbookDocument } from '../../core/model';
import type { LocalDateTime } from '../../core/local-datetime';
import { CategoryIcon, Glyph, PinRing } from '../icons';
import { formatArrival, formatDistance, formatDuration, formatElevation, NOT_AVAILABLE } from '../format';
import { ACTIONS } from '../labels';
import { expandedRows, toggleRowExpanded } from '../state';
import { PoiDetail } from './PoiDetail';

const FAMILY_CLASS: Readonly<Record<string, string>> = {
  water: 'family-water',
  supply: 'family-supply',
  rest: 'family-rest',
  race: 'family-race',
};

export interface RaceCardProps {
  readonly poi: Poi;
  readonly document: RoadbookDocument;
  readonly passageIndex: number;
  /** Rang de la carte, 0 pour le POI mis en évidence. */
  readonly rank: number;
  readonly total: number;
  /** Distance restante jusqu'au POI, en mètres, ou `null` sans position. */
  readonly remainingMeters: number | null;
  /** D+ restant jusqu'au POI, ou `null` si l'altitude est indisponible. */
  readonly remainingGainMeters: number | null;
  /** Durée restante avant de l'atteindre, en secondes. */
  readonly remainingSeconds: number | null;
  readonly arrival: LocalDateTime | null;
  readonly startLocal: LocalDateTime | null;
  /** Le POI précédent occupe la même position : les valeurs sont identiques. */
  readonly sameSpot: boolean;
}

export function RaceCard(props: RaceCardProps): JSX.Element {
  const { poi } = props;
  const category = nominalCategory(poi);
  const family = FAMILY_CLASS[categoryFamily(category)] ?? '';
  const icon = <CategoryIcon category={category} pinned={poi.userState.pinned} decorative />;
  const name = poi.name ?? categoryLabel(category);

  const key = `race-${poi.id}-${props.passageIndex}`;
  const expanded = expandedRows.value.has(key);
  const label = props.rank === 0 ? 'Prochain POI' : `${props.rank + 1}ᵉ POI`;

  return (
    <article class={props.rank === 0 ? 'race-card race-card--next' : 'race-card'}>
      <div class="race-card__label">
        {/* Le chevron est à gauche, comme dans la liste du roadbook. */}
        <button
          type="button"
          class="race-card__toggle"
          aria-expanded={expanded}
          onClick={() => {
            toggleRowExpanded(key);
          }}
        >
          <Glyph name="suivant" label={expanded ? ACTIONS.hideDetail : ACTIONS.showDetail} size={26} />
        </button>
        <span>{label}</span>
        {props.sameSpot ? <span class="race-card__spot">même arrêt</span> : null}
        <span class="race-card__rank">
          {props.rank + 1} / {props.total}
        </span>
      </div>

      <div class="race-card__who">
        <span class={family}>{poi.userState.pinned ? <PinRing>{icon}</PinRing> : icon}</span>
        <div>
          <div class="race-card__name">{name}</div>
          <div class="muted">
            {categoryLabel(category)}
            {poi.passages.length > 1 ? ` · passage ${props.passageIndex}/${poi.passages.length}` : ''}
            {poi.openingHours !== null ? ` · ${poi.openingHours}` : ''}
          </div>
        </div>
      </div>

      <dl class="race-card__metrics">
        <Metric
          label="Distance"
          value={props.remainingMeters === null ? NOT_AVAILABLE : formatDistance(props.remainingMeters)}
        />
        <Metric
          label="D+ restant"
          value={props.remainingGainMeters === null ? NOT_AVAILABLE : formatElevation(props.remainingGainMeters)}
        />
        <Metric
          label="Dans"
          value={props.remainingSeconds === null ? NOT_AVAILABLE : formatDuration(props.remainingSeconds)}
        />
        <Metric label="Arrivée" value={formatArrival(props.arrival, props.startLocal)} small />
      </dl>

      {expanded ? (
        <div class="race-card__detail">
          <PoiDetail poi={poi} document={props.document} />
        </div>
      ) : null}
    </article>
  );
}

function Metric(props: {
  readonly label: string;
  readonly value: string;
  readonly small?: boolean;
}): JSX.Element {
  return (
    <div class="race-metric">
      <dt>{props.label}</dt>
      <dd class={props.small === true ? 'race-metric__value race-metric__value--small' : 'race-metric__value'}>
        {props.value}
      </dd>
    </div>
  );
}
